import React, { useState, useRef } from 'react';
import {
    Box,
    Typography,
    Paper,
    Card,
    CardContent,
    Button,
    Grid,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    TextField,
    Divider,
    Chip,
    Stack,
    Slider,
    Switch,
    MenuItem,
    Select,
    InputLabel,
    Tooltip,
    IconButton,
    Alert,
    AlertTitle,
    Collapse,
    FormGroup,
    Checkbox,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import {
    Download,
    Share2,
    User,
    Globe,
    Sliders,
    ChevronDown,
    ChevronUp,
    Copy,
    HelpCircle,
    Info,
    BarChart,
    MapPin,
    Mail,
    Phone,
    ExternalLink,
    Briefcase,
    Camera,
    Wand2,
    Sparkles

} from 'lucide-react';

import { useNavigate } from 'react-router-dom';
import DesignerQRCode from './ProfilePageDesignerQR';
//const EnhancedQRCodeGenerator = ({ profileData, getProfileSlug, isTrial = false }) => {
const EnhancedQRCodeGenerator = (props) => {
    const navigate = useNavigate();
    const { profileData, getProfileSlug, isTrial } = props;
    // State for QR code settings
    const [qrType, setQrType] = useState('contact');
    const [qrSubtype, setQrSubtype] = useState('full'); // full, mini, contact
    const [qrForeground, setQrForeground] = useState('#000000');
    const [qrBackground, setQrBackground] = useState('#ffffff');
    const [qrSize, setQrSize] = useState(200);
    const [qrLogo, setQrLogo] = useState(false);
    const [qrIncludeFrame, setQrIncludeFrame] = useState(false);
    const [qrErrorCorrection, setQrErrorCorrection] = useState('H');
    const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
    const [trackingEnabled, setTrackingEnabled] = useState(false);
    const [showAnalytics, setShowAnalytics] = useState(false);
    const [customSubdomain, setCustomSubdomain] = useState('');
    const [showHttps, setShowHttps] = useState(true);
    const [showDesignerQR, setShowDesignerQR] = useState(false);
    const [designerQRUrl, setDesignerQRUrl] = useState(null);
    const [isUsingDesignerQR, setIsUsingDesignerQR] = useState(false);
    // Field selection for custom QR and vCard
    const [selectedFields, setSelectedFields] = useState({
        name: true,
        title: true,
        company: true,
        email: true,
        phone: true,
        linkedin: true,
        location: true,
        website: true
    });

    const qrCodeRef = useRef(null);
    const handleDesignerQRGenerated = (url) => {
        setDesignerQRUrl(url);
        setIsUsingDesignerQR(true);
    };
    // Mock analytics data for tracking feature preview
    const scanStats = {
        totalScans: 143,
        uniqueScans: 87,
        lastScan: '2 hours ago',
        topLocations: ['United States', 'Germany', 'India', 'United Kingdom']
    };

    // Get QR data based on type
    const getQRData = () => {
        // Add tracking ID for any URL-based QR if tracking is enabled
        const trackingParam = trackingEnabled ? `trackingId=QR-${Date.now().toString(36)}` : '';
        const addTracking = (url) => {
            if (!trackingEnabled) return url;
            return url.includes('?') ? `${url}&${trackingParam}` : `${url}?${trackingParam}`;
        };

        if (qrType === 'profile') {
            let url;
            if (qrSubtype === 'full') {
                url = `https://${profileData.profileBaseUrl}/profile/${getProfileSlug()}`;
            } else if (qrSubtype === 'mini') {
                url = `https://${profileData.profileBaseUrl}/p/${getProfileSlug()}`;
            } else {
                url = `https://${profileData.profileBaseUrl}/c/${getProfileSlug()}`;
            }
            return addTracking(url);
        } else if (qrType === 'contact') {
            return generateVCard();
        } else if (qrType === 'domain') {
            // Company domain QR code
            let domain = profileData.companyUrl || `https://${profileData.domain}`;

            // Check if domain already has https:// or http://
            if (!domain.startsWith('http')) {
                domain = showHttps ? `https://${domain}` : `http://${domain}`;
            }

            // Add custom subdomain if provided
            if (customSubdomain) {
                // Extract the base domain (remove protocol and www if present)
                let baseDomain = domain.replace(/^https?:\/\//, '');
                if (baseDomain.startsWith('www.')) {
                    baseDomain = baseDomain.substring(4);
                }

                // Create new URL with subdomain
                domain = `${showHttps ? 'https' : 'http'}://${customSubdomain}.${baseDomain}`;
            }

            return addTracking(domain);
        } else if (qrType === 'custom') {
            // For custom QR, create a URL with parameters for maximum compatibility
            return generateCustomURL();
        }

        return `https://${profileData.profileBaseUrl}/profile/${getProfileSlug()}`;
    };

    // Generate vCard data - fixed formatting for proper mobile contact display
    const generateVCard = () => {
        // Parse the name to extract first/last name
        const nameParts = profileData.name.split(' ');
        const lastName = nameParts.length > 1 ? nameParts.pop() : '';
        const firstName = nameParts.join(' ');

        let vcard = 'BEGIN:VCARD\nVERSION:3.0\n';

        // FN is the formatted name (display name)
        vcard += `FN:${profileData.name}\n`;

        // N is structured name (lastName;firstName;middleName;title;suffix)
        vcard += `N:${lastName};${firstName};;;\n`;

        if (selectedFields.title && profileData.title) {
            vcard += `TITLE:${profileData.title}\n`;
        }

        if (selectedFields.company && profileData.company) {
            vcard += `ORG:${profileData.company}\n`;
        }

        if (selectedFields.email && profileData.email) {
            vcard += `EMAIL;type=WORK:${profileData.email}\n`;
        }

        if (selectedFields.phone && profileData.phone) {
            vcard += `TEL;type=CELL:${profileData.phone}\n`;
        }

        if (selectedFields.location && profileData.companyAddress) {
            vcard += `ADR;type=WORK:;;${profileData.companyAddress}\n`;
        }

        if (selectedFields.website && profileData.companyUrl) {
            vcard += `URL;type=WORK:${profileData.companyUrl}\n`;
        }

        if (selectedFields.linkedin && profileData.linkedinUrl) {
            vcard += `URL;type=LINKEDIN:${profileData.linkedinUrl}\n`;
        }

        // Add tracking URL if enabled
        if (trackingEnabled) {
            const trackingId = `QR-${Date.now().toString(36)}`;
            vcard += `URL;type=TRACKING:https://${profileData.profileBaseUrl}/track/${trackingId}\n`;
        }

        vcard += 'END:VCARD';
        return vcard;
    };

    // Generate custom QR - create a URL for better scanning compatibility
    const generateCustomURL = () => {
        // Create a URL with query parameters for better scanning reliability
        const baseUrl = `https://${profileData.profileBaseUrl}/profile/custom`;
        const params = new URLSearchParams();

        if (selectedFields.name && profileData.name) {
            params.append("name", profileData.name);
        }

        if (selectedFields.title && profileData.title) {
            params.append("title", profileData.title);
        }

        if (selectedFields.company && profileData.company) {
            params.append("company", profileData.company);
        }

        if (selectedFields.email && profileData.email) {
            params.append("email", profileData.email);
        }

        if (selectedFields.phone && profileData.phone) {
            params.append("phone", profileData.phone);
        }

        if (selectedFields.location && profileData.companyAddress) {
            params.append("address", profileData.companyAddress);
        }

        if (selectedFields.website && profileData.companyUrl) {
            params.append("website", profileData.companyUrl);
        }

        if (selectedFields.linkedin && profileData.linkedinUrl) {
            params.append("linkedin", profileData.linkedinUrl);
        }

        // Add tracking ID if enabled
        if (trackingEnabled) {
            params.append("trackingId", `QR-${Date.now().toString(36)}`);
        }

        return `${baseUrl}?${params.toString()}`;
    };
    // Handle QR code download
    const downloadQR = () => {
        try {
            // Get the SVG element
            const svg = document.getElementById('qr-code');
            if (!svg) {
                throw new Error('QR code element not found');
            }

            // Convert SVG to string
            const svgData = new XMLSerializer().serializeToString(svg);

            // Create a canvas element to convert SVG to PNG
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            img.onload = () => {
                canvas.width = img.width;
                canvas.height = img.height;
                ctx.drawImage(img, 0, 0);

                // Convert canvas to PNG data URL
                const pngUrl = canvas.toDataURL('image/png');

                // Create download link
                const downloadLink = document.createElement('a');
                downloadLink.href = pngUrl;
                downloadLink.download = `${profileData.name.toLowerCase().replace(/\s+/g, '-')}-${qrType}-qr.png`;
                document.body.appendChild(downloadLink);
                downloadLink.click();
                document.body.removeChild(downloadLink);
            };

            img.onerror = () => {
                throw new Error('Error loading QR code image');
            };

            // Create a data URL from the SVG
            img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
        } catch (error) {
            console.error('Error downloading QR code:', error);
        }
    };

    // Copy QR link to clipboard
    const copyQRLink = () => {
        navigator.clipboard.writeText(getQRData());
        // Add notification here if desired
    };

    // Helper function to get QR code description based on type
    const getQRDescription = () => {
        if (qrType === 'profile') {
            if (qrSubtype === 'full') {
                return "Full profile page with all your professional details";
            } else if (qrSubtype === 'mini') {
                return "Compact version of your profile with essential information";
            } else {
                return "Focused on your contact information for quick connections";
            }
        } else if (qrType === 'contact') {
            return "Digital business card format (vCard) that can be saved to phone contacts";
        } else if (qrType === 'domain') {
            return "Direct link to your company website or specific subdomain";
        } else {
            return "Customized QR code with only the information you select";
        }
    };

    // Toggle specific field selection
    const toggleField = (field) => {
        setSelectedFields({
            ...selectedFields,
            [field]: !selectedFields[field]
        });
    };

    // Get the included fields as a list
    const getIncludedFields = () => {
        return Object.entries(selectedFields)
            .filter(([_, selected]) => selected)
            .map(([field]) => field.charAt(0).toUpperCase() + field.slice(1));
    };

    // Get domain info for display
    const getDomainPreview = () => {
        let domain = profileData.companyUrl || profileData.domain || "company.com";

        // Remove protocol
        domain = domain.replace(/^https?:\/\//, '');

        // Add custom subdomain if provided
        if (customSubdomain) {
            domain = `${customSubdomain}.${domain}`;
        }

        // Add protocol back
        return `${showHttps ? 'https' : 'http'}://${domain}`;
    };

    // Common Card Props
    const cardProps = {
        elevation: 3,
        sx: {
            height: '100%',
            borderRadius: 2,
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 8,
            },
            '& .MuiCardContent-root': {
                height: '100%',
                p: 3
            }
        }
    };


return (
    <Grid container spacing={3}>
        {/* QR Code Generator */}
        <Grid item xs={12} md={6}>
            <Card {...cardProps}>
                <CardContent>
                    <Box className="flex justify-between items-center mb-4">
                        <Typography variant="h6" className="font-semibold">
                            Generate QR Code
                        </Typography>
                        <Tooltip title="Choose what information to share">
                            <IconButton size="small">
                                <HelpCircle className="w-4 h-4" />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    <Stack spacing={3}>
                        {/* Content for first card remains unchanged */}
                        {/* ... */}
                        
                        {/* All your existing code for the first card */}
                        <Box>
                                <Typography className="mb-2" color="textSecondary">
                                    Select QR Code Type
                                </Typography>
                                <Grid container spacing={1}>
                                    <Grid item xs={6} sm={3}>
                                        <Button
                                            variant={qrType === 'profile' ? 'contained' : 'outlined'}
                                            onClick={() => {
                                                setQrType('profile');
                                                if (isTrial) setTrackingEnabled(false);
                                            }}
                                            startIcon={<User size={16} />}
                                            fullWidth
                                            sx={{ mb: 1 }}
                                        >
                                            Profile
                                        </Button>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Button
                                            variant={qrType === 'contact' ? 'contained' : 'outlined'}
                                            onClick={() => {
                                                setQrType('contact');
                                                if (isTrial) setTrackingEnabled(false);
                                            }}
                                            startIcon={<Phone size={16} />}
                                            fullWidth
                                            sx={{ mb: 1 }}
                                        >
                                            Contact
                                        </Button>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Button
                                            variant={qrType === 'domain' ? 'contained' : 'outlined'}
                                            onClick={() => {
                                                setQrType('domain');
                                                if (isTrial) setTrackingEnabled(false);
                                            }}
                                            startIcon={<Globe size={16} />}
                                            fullWidth
                                            sx={{ mb: 1 }}
                                        >
                                            Company URL
                                        </Button>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Button
                                            variant={qrType === 'custom' ? 'contained' : 'outlined'}
                                            onClick={() => {
                                                setQrType('custom');
                                                if (isTrial) {
                                                    // Show upgrade message for trial users
                                                    navigate('/profiles/upgrade');
                                                }
                                            }}
                                            startIcon={<Sliders size={16} />}
                                            fullWidth
                                            sx={{ mb: 1 }}
                                        >
                                            Custom
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Type-specific Options - Show to all but make trial users unable to use them */}
                            {qrType === 'profile' && (
                                <Box>
                                    <FormControl component="fieldset">
                                        <RadioGroup
                                            row
                                            value={qrSubtype}
                                            onChange={(e) => setQrSubtype(e.target.value)}
                                        >
                                            <FormControlLabel
                                                value="full"
                                                control={<Radio disabled={isTrial} />}
                                                label="Full Profile"
                                            />
                                            <FormControlLabel
                                                value="mini"
                                                control={<Radio disabled={isTrial} />}
                                                label="Mini Profile"
                                            />
                                            <FormControlLabel
                                                value="contact"
                                                control={<Radio disabled={isTrial} />}
                                                label="Contact Page"
                                            />
                                        </RadioGroup>
                                    </FormControl>

                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        {getQRDescription()}
                                    </Typography>
                                </Box>
                            )}

                            {/* Domain URL Options */}
                            {qrType === 'domain' && (
                                <Box>
                                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
                                        Company Website Options {isTrial && "(Premium Feature)"}
                                    </Typography>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={showHttps}
                                                        onChange={(e) => !isTrial && setShowHttps(e.target.checked)}
                                                        disabled={isTrial}
                                                    />
                                                }
                                                label="Use HTTPS (Secure)"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                label="Subdomain (Optional)"
                                                placeholder="www"
                                                value={customSubdomain}
                                                onChange={(e) => !isTrial && setCustomSubdomain(e.target.value)}
                                                disabled={isTrial}
                                                fullWidth
                                                helperText={`URL will be: ${getDomainPreview()}`}
                                                InputProps={{
                                                    startAdornment: <Globe size={16} style={{ marginRight: 8, opacity: 0.7 }} />,
                                                }}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        {getQRDescription()}
                                    </Typography>
                                </Box>
                            )}

                            {/* Field Selection for Contact or Custom QR */}
                            {(qrType === 'contact' || qrType === 'custom') && (
                                <Box>
                                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
                                        Select Information to Include {isTrial && "(Premium Feature)"}
                                    </Typography>
                                    <FormGroup>
                                        <Grid container spacing={1}>
                                            {Object.entries(selectedFields).map(([field, selected]) => (
                                                <Grid item xs={6} key={field}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={selected}
                                                                onChange={() => !isTrial && toggleField(field)}
                                                                size="small"
                                                                disabled={isTrial}
                                                            />
                                                        }
                                                        label={field.charAt(0).toUpperCase() + field.slice(1)}
                                                    />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </FormGroup>
                                </Box>
                            )}

                            {/* Advanced QR Options - Show to all but disable for trial */}
                            <Box>
                                <Button
                                    endIcon={showAdvancedSettings ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                    onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                                    variant="text"
                                    startIcon={<Sliders size={16} />}
                                >
                                    Advanced Options {isTrial && "(Premium)"}
                                </Button>

                                <Collapse in={showAdvancedSettings}>
                                    <Grid container spacing={2} sx={{ mt: 1 }}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" gutterBottom>
                                                Foreground Color
                                            </Typography>
                                            <TextField
                                                type="color"
                                                value={qrForeground}
                                                onChange={(e) => !isTrial && setQrForeground(e.target.value)}
                                                size="small"
                                                fullWidth
                                                disabled={isTrial}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" gutterBottom>
                                                Background Color
                                            </Typography>
                                            <TextField
                                                type="color"
                                                value={qrBackground}
                                                onChange={(e) => !isTrial && setQrBackground(e.target.value)}
                                                size="small"
                                                fullWidth
                                                disabled={isTrial}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="body2" gutterBottom>
                                                QR Size
                                            </Typography>
                                            <Slider
                                                value={qrSize}
                                                onChange={(e, newValue) => !isTrial && setQrSize(newValue)}
                                                min={100}
                                                max={300}
                                                step={10}
                                                valueLabelDisplay="auto"
                                                disabled={isTrial}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={qrLogo}
                                                        onChange={(e) => !isTrial && setQrLogo(e.target.checked)}
                                                        disabled={isTrial}
                                                    />
                                                }
                                                label="Include Profile Photo"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Error Correction</InputLabel>
                                                <Select
                                                    value={qrErrorCorrection}
                                                    onChange={(e) => !isTrial && setQrErrorCorrection(e.target.value)}
                                                    label="Error Correction"
                                                    disabled={isTrial}
                                                >
                                                    <MenuItem value="L">Low (7%)</MenuItem>
                                                    <MenuItem value="M">Medium (15%)</MenuItem>
                                                    <MenuItem value="Q">Quartile (25%)</MenuItem>
                                                    <MenuItem value="H">High (30%)</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={trackingEnabled}
                                                        onChange={(e) => {
                                                            if (isTrial) {
                                                                navigate('/profiles/upgrade');
                                                            } else {
                                                                setTrackingEnabled(e.target.checked);
                                                                if (e.target.checked) {
                                                                    setShowAnalytics(true);
                                                                }
                                                            }
                                                        }}
                                                        disabled={isTrial}
                                                    />
                                                }
                                                label={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        Track Scans
                                                        <Tooltip title="Add analytics to track when and where your QR code is scanned">
                                                            <Info size={16} />
                                                        </Tooltip>
                                                    </Box>
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Collapse>
                            </Box>

                            <Box
                                className="flex justify-center"
                                sx={{
                                    position: 'relative',
                                    borderRadius: 2,
                                    overflow: 'hidden'
                                }}
                            >
                                <Paper elevation={3} className="p-6 bg-white">
                                    <QRCodeSVG
                                        id="qr-code"
                                        ref={qrCodeRef}
                                        value={getQRData()}
                                        size={isTrial ? 200 : qrSize}
                                        bgColor={isTrial ? "#ffffff" : qrBackground}
                                        fgColor={isTrial ? "#000000" : qrForeground}
                                        level={isTrial ? "H" : qrErrorCorrection}
                                        includeMargin={true}
                                        imageSettings={!isTrial && qrLogo ? {
                                            src: qrType === 'domain' && profileData.company
                                                ? "https://logo.clearbit.com/" + profileData.domain.replace(/^https?:\/\//, '').split('/')[0]
                                                : profileData.avatar,
                                            excavate: true,
                                            width: qrSize * 0.25,
                                            height: qrSize * 0.25
                                        } : undefined}
                                    />
                                </Paper>

                                {/* Trial overlay - only show for trial users */}
                                {isTrial && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            bgcolor: 'rgba(255, 255, 255, 0.6)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            zIndex: 10,
                                            backdropFilter: 'blur(2px)',
                                            borderRadius: 2,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                bgcolor: 'white',
                                                p: 3,
                                                borderRadius: 2,
                                                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                                maxWidth: '80%',
                                                textAlign: 'center'
                                            }}
                                        >
                                            <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                                                Premium Feature
                                            </Typography>
                                            <Typography variant="body1" paragraph>
                                                QR code generation is available only in the full version.
                                            </Typography>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                onClick={() => navigate('/profiles/upgrade')}
                                            >
                                                Upgrade Now
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                            </Box>

                            <Stack direction="row" spacing={2}>
                                <Button
                                    variant="outlined"
                                    startIcon={<Copy />}
                                    onClick={isTrial ? () => navigate('/profiles/upgrade') : copyQRLink}
                                    fullWidth
                                    disabled={isTrial}
                                >
                                    {isTrial ? "Premium Feature" : "Copy Link"}
                                </Button>

                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<Download />}
                                    onClick={isTrial ? () => navigate('/profiles/upgrade') : downloadQR}
                                    fullWidth
                                    disabled={isTrial}
                                >
                                    {isTrial ? "Upgrade to Download" : "Download QR Code"}
                                </Button>
                            </Stack>

                            <Typography variant="body2" color="text.secondary" align="center">
                                {qrType === 'profile'
                                    ? `This QR code links to: https://${profileData.profileBaseUrl}/profile/${getProfileSlug()}`
                                    : qrType === 'contact'
                                        ? 'This QR code contains your contact details for mobile contacts'
                                        : qrType === 'domain'
                                            ? `This QR code links to: ${getDomainPreview()}`
                                            : 'This QR code contains your customized information'}
                            </Typography>

                            {/* Analytics Preview Section - Show to everyone but only functional for non-trial */}
                            {trackingEnabled && !isTrial && (
                                <Accordion sx={{ mt: 2 }}>
                                    <AccordionSummary expandIcon={<ChevronDown />}>
                                        <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <BarChart size={18} />
                                            QR Code Analytics
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={6} sm={3}>
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Total Scans
                                                    </Typography>
                                                    <Typography variant="h6">
                                                        {scanStats.totalScans}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6} sm={3}>
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Unique Visitors
                                                    </Typography>
                                                    <Typography variant="h6">
                                                        {scanStats.uniqueScans}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6} sm={3}>
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Last Scanned
                                                    </Typography>
                                                    <Typography variant="h6">
                                                        {scanStats.lastScan}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6} sm={3}>
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Conversion Rate
                                                    </Typography>
                                                    <Typography variant="h6">
                                                        {Math.round((scanStats.uniqueScans / scanStats.totalScans) * 100)}%
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>

                                        <Divider sx={{ my: 2 }} />

                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            Top Locations
                                        </Typography>
                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                            {scanStats.topLocations.map((location, index) => (
                                                <Chip
                                                    key={index}
                                                    label={location}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            ))}
                                        </Stack>
                                    </AccordionDetails>
                                </Accordion>
                            )}

                            {/* Tracking Feature Preview for Trial */}
                            {isTrial && (
                                <Box
                                    sx={{
                                        mt: 2,
                                        p: 2,
                                        border: '1px dashed #3f51b5',
                                        borderRadius: 1,
                                        bgcolor: 'rgba(63, 81, 181, 0.05)'
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            color: '#3f51b5'
                                        }}
                                    >
                                        <BarChart size={18} />
                                        Premium Feature: QR Scan Analytics
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                        Upgrade to track when and where your QR codes are scanned. Get insights about who is viewing your profile.
                                    </Typography>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        sx={{ mt: 1 }}
                                        onClick={() => navigate('/profiles/upgrade')}
                                    >
                                        Upgrade to Access
                                    </Button>
                                </Box>
                            )}
                    </Stack>
                </CardContent>
            </Card>
        </Grid>

        {/* QR Code Preview */}
        <Grid item xs={12} md={6}>
            <Card {...cardProps}>
                <CardContent>
                    <Typography variant="h6" className="font-semibold mb-4">
                        QR Code Preview
                    </Typography>
                    <Box className="bg-gray-100 p-4 rounded-lg">
                        <Typography variant="subtitle2" className="mb-2">
                            Information included in {qrType === 'profile' ? 'Profile Link' :
                                qrType === 'contact' ? 'Contact Card' :
                                    qrType === 'domain' ? 'Company URL' : 'Custom QR'}:
                        </Typography>
                        <Stack spacing={2}>
                        {qrType === 'profile' ? (
                                    <>
                                        <Chip label="Full Profile Details" color="primary" />
                                        <Chip label="Professional Background" />
                                        <Chip label="Skills & Expertise" />
                                        <Chip label="Contact Information" />
                                    </>
                                ) : qrType === 'contact' ? (
                                    <>
                                        <Chip label="Name & Title" color="primary" />
                                        <Chip label="Company Details" />
                                        <Chip label="Email & Phone" />
                                        <Chip label="Business Address" />
                                    </>
                                ) : qrType === 'domain' ? (
                                    <>
                                        <Chip label="Company Website" color="primary" />
                                        <Chip label="Direct URL Link" />
                                        {customSubdomain && <Chip label={`Subdomain: ${customSubdomain}`} />}
                                        {trackingEnabled && <Chip label="Visitor Tracking" />}
                                    </>
                                ) : (
                                    <>
                                        {getIncludedFields().map((field, index) => (
                                            <Chip
                                                key={index}
                                                label={field}
                                                color={index === 0 ? "primary" : "default"}
                                            />
                                        ))}
                                    </>
                                )}
                        </Stack>
                    </Box>

                    {/* QR Code Sample Usage */}
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
                            How to use your QR code:
                        </Typography>

                        <Grid container spacing={2} sx={{ mt: 1 }}>
                        {qrType === 'domain' ? (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                p: 2,
                                                borderRadius: 1,
                                                bgcolor: 'rgba(245, 245, 245, 0.8)',
                                                height: '100%'
                                            }}>
                                                <Briefcase size={24} color="#3f51b5" style={{ marginBottom: 8 }} />
                                                <Typography variant="body2" align="center" sx={{ fontWeight: 'medium' }}>
                                                    Marketing Materials
                                                </Typography>
                                                <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 1 }}>
                                                    Add to brochures and product packaging
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                p: 2,
                                                borderRadius: 1,
                                                bgcolor: 'rgba(245, 245, 245, 0.8)',
                                                height: '100%'
                                            }}>
                                                <ExternalLink size={24} color="#3f51b5" style={{ marginBottom: 8 }} />
                                                <Typography variant="body2" align="center" sx={{ fontWeight: 'medium' }}>
                                                    Direct Web Traffic
                                                </Typography>
                                                <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 1 }}>
                                                    Drive visitors directly to your website
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </>
                                ) : (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                p: 2,
                                                borderRadius: 1,
                                                bgcolor: 'rgba(245, 245, 245, 0.8)',
                                                height: '100%'
                                            }}>
                                                <Mail size={24} color="#3f51b5" style={{ marginBottom: 8 }} />
                                                <Typography variant="body2" align="center" sx={{ fontWeight: 'medium' }}>
                                                    Email Signature
                                                </Typography>
                                                <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 1 }}>
                                                    Add to your email signature for easy contact sharing
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                p: 2,
                                                borderRadius: 1,
                                                bgcolor: 'rgba(245, 245, 245, 0.8)',
                                                height: '100%'
                                            }}>
                                                <Phone size={24} color="#3f51b5" style={{ marginBottom: 8 }} />
                                                <Typography variant="body2" align="center" sx={{ fontWeight: 'medium' }}>
                                                    Digital Business Card
                                                </Typography>
                                                <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 1 }}>
                                                    Share during networking events
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        </Box>
                          {/* <Box sx={{ mt: 4 }}>
                        <Divider>
                            <Chip
                                label="ARTISTIC QR CODE"
                                color="primary"
                                icon={<Sparkles size={16} />}
                            />
                        </Divider>

                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'medium', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Wand2 size={18} />
                                    Artistic QR Codes
                                </Typography>

                                <Button
                                    variant="text"
                                    endIcon={showDesignerQR ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                    onClick={() => {
                                        if (isTrial) {
                                            navigate('/profiles/upgrade');
                                        } else {
                                            setShowDesignerQR(!showDesignerQR);
                                        }
                                    }}
                                >
                                    {showDesignerQR ? 'Hide' : 'Show Options'}
                                </Button>
                            </Box>

                            {!showDesignerQR && (
                                <Alert
                                    severity="info"
                                    icon={<Sparkles />}
                                    sx={{ mt: 2 }}
                                >
                                    <AlertTitle>Create Unique Brand QR Codes</AlertTitle>
                                    Design QR codes that incorporate your profile photo or company logo directly into the QR pattern itself.
                                    {isTrial && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            sx={{ mt: 1 }}
                                            onClick={() => navigate('/profiles/upgrade')}
                                        >
                                            Upgrade to Access
                                        </Button>
                                    )}
                                </Alert>
                            )}

                            <Collapse in={showDesignerQR && !isTrial}>
                                <Box sx={{ mt: 2 }}>
                                    <DesignerQRCode
                                        qrValue={getQRData()}
                                        profileData={profileData}
                                        isTrial={isTrial}
                                        navigate={navigate}
                                        onGenerated={handleDesignerQRGenerated}
                                        size={300}
                                    />
                                </Box>


                            
                            {isUsingDesignerQR && designerQRUrl && !isTrial && (
                                <Box sx={{
                                    mt: 3,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center'
                                }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Your Artistic QR Code:
                                    </Typography>
                                    <Box
                                        component="img"
                                        src={designerQRUrl}
                                        alt="Designer QR Code"
                                        sx={{
                                            maxWidth: '100%',
                                            maxHeight: 200,
                                            border: '1px solid #e0e0e0',
                                            borderRadius: 1,
                                            p: 1,
                                            bgcolor: '#ffffff'
                                        }}
                                    />
                                </Box>
                            )}
                                                        </Collapse>
                        </Box>
                    </Box> */}

                    {isTrial && (
                        <Alert
                            severity="info"
                            sx={{ mt: 3 }}
                            icon={<Camera />}
                        >
                            <AlertTitle>Trial Limitation</AlertTitle>
                            During your trial period, you can see a preview of your QR code but download functionality is restricted.
                            <Button
                                size="small"
                                color="primary"
                                variant="outlined"
                                onClick={() => navigate('/profiles/upgrade')}
                                sx={{ mt: 1 }}
                            >
                                Upgrade to Full Version
                            </Button>
                        </Alert>
                    )}
                </CardContent>
            </Card>
        </Grid>
    </Grid>
);
};

export default EnhancedQRCodeGenerator;